import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";

const CATEGORIES = [
  "Wrong or missing data",
  "Feature idea",
  "Bug report",
  "General feedback",
];

const MAX_LENGTH = 2000;

interface ContactFormProps {
  onClose: () => void;
  userId?: string;
}

export default function ContactForm({ onClose, userId }: ContactFormProps) {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      await addDoc(collection(db, "contactMessages"), {
        category,
        message: message.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(userId ? { userId } : {}),
        createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch {
      setError("Unable to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-bg max-w-md w-full rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          {sent ? (
            <div className="text-center py-6">
              <p className="text-lg font-semibold text-text mb-1">Thanks!</p>
              <p className="text-sm text-muted font-[family-name:--font-ui]">Your feedback helps us improve.</p>
              <button
                onClick={onClose}
                className="mt-5 px-6 py-2 border border-border text-text rounded-md font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-lg font-semibold text-text mb-1">Send Feedback</h2>
              <p className="text-sm text-muted mb-5">
                Found wrong data, have a feature idea, or spotted a bug? Let us know.
              </p>

              {/* Category */}
              <div className="mb-4">
                <label className="section-label mb-1.5 block">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-bg text-text text-sm font-[family-name:--font-ui] focus:border-accent outline-none appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%238a8580' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2 4 4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="section-label mb-1.5 block">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                  required
                  rows={5}
                  placeholder="Tell us what happened, what product you were looking at, or what you'd like to see..."
                  className="w-full px-3 py-2 border border-border rounded-md bg-bg text-text text-sm placeholder:text-muted focus:border-accent outline-none resize-y"
                />
                <p className="text-right text-[11px] text-muted font-[family-name:--font-ui] mt-1">
                  {message.length} / {MAX_LENGTH}
                </p>
              </div>

              {/* Email (optional) */}
              <div className="mb-5">
                <label className="section-label mb-1.5 block">
                  Email <span className="font-normal text-muted">(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Only if you'd like a reply"
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-bg text-text text-sm placeholder:text-muted focus:border-accent outline-none"
                />
                <p className="text-[11px] text-muted font-[family-name:--font-ui] mt-1">
                  We'll only use this to respond to your feedback.
                </p>
              </div>

              {error && (
                <p className="text-sm text-gi-high font-[family-name:--font-ui] mb-4">{error}</p>
              )}

              <button
                type="submit"
                disabled={sending || !message.trim()}
                className="px-6 py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="spinner spinner--sm"></div>
                    Sending...
                  </>
                ) : (
                  "Send Feedback"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
